import { ProbabilityCategory } from './ProbabilityCategory';

/*
 * Marker-severity helpers shared by the standard-sweep likelihood gauges
 * (Stress & Adrenal, Iron). Each gauge decides for itself WHICH markers count
 * and in which direction; this module only answers how far one marker is off
 * and how a tally of such markers reads on the 0-100 sweep.
 *
 * Extracted from StressAdrenalAnalysis.ts (2026-09-20) when the Iron gauge
 * needed the same ladder. The bodies are unchanged; the adrenal tests still
 * pin them.
 *
 * Every helper compares raw `value` / `range` fields rather than calling
 * PanelMarker's methods, so a gauge stays testable with plain
 * `{ value, range, hasValue }` objects.
 */

/** Likelihood word for a needle %, using the Glucose gauge thresholds
 *  (green < 50 Less Likely · 50–69 Possible · 70–89 Likely · ≥ 90 Highly Likely). */
export function probabilityCategory(p: number): ProbabilityCategory {
  if (p >= 90) return ProbabilityCategory.HIGHLY_LIKELY;
  if (p >= 70) return ProbabilityCategory.LIKELY;
  if (p >= 50) return ProbabilityCategory.POSSIBLE;
  return ProbabilityCategory.LESS_LIKELY;
}

/**
 * Severity rank (1 Possible · 2 Likely · 3 Highly Likely) of a marker deviating
 * in `direction`, or null when the marker is unmeasured, range-less, or not
 * deviating that way. Alarm bounds fall back to the lab bounds when undefined,
 * which makes an out-of-lab value read past alarm (rank 3). In the app every
 * range has alarm bounds (PanelRange.init derives the missing ones), so the
 * fallback only ever meets a hand-built test fixture; give fixtures alarms.
 */
export function deviationRank(marker: any, direction: 'low' | 'high'): number | null {
  if (!marker?.hasValue?.() || !marker.range) return null;
  const r = marker.range;
  const v = marker.value;
  if (direction === 'low') {
    if (!(v < r.optimalMin)) return null;
    const alarmMin = r.alarmMin ?? r.labMin;
    if (v < alarmMin) return 3;
    if (v < r.labMin) return 2;
    return 1;
  }
  if (!(v > r.optimalMax)) return null;
  const alarmMax = r.alarmMax ?? r.labMax;
  if (v > alarmMax) return 3;
  if (v > r.labMax) return 2;
  return 1;
}

/**
 * Whether a marker deviating in `direction` sits past the lab↔optimal midpoint
 * (avgMax high / avgMin low) — i.e. more than half-way from optimal toward the
 * lab bound. Used to grade the Possible tier (a within-lab deviation) the same
 * way the Glucose gauge does, so a marker barely past optimal reads lower than
 * one nearing the lab bound.
 */
export function isPastMidpoint(marker: any, direction: 'low' | 'high'): boolean {
  if (!marker?.hasValue?.() || !marker.range) return false;
  const r = marker.range;
  const v = marker.value;
  if (direction === 'low') return v < (r.optimalMin + r.labMin) / 2;
  return v > (r.optimalMax + r.labMax) / 2;
}

/** Direction a marker deviates in, or null when optimal / unmeasured / range-less. */
export function deviationDirection(marker: any): 'high' | 'low' | null {
  if (deviationRank(marker, 'low') != null) return 'low';
  if (deviationRank(marker, 'high') != null) return 'high';
  return null;
}

/** The counts a gauge accumulates over the markers it decided to score. */
export interface SeverityTally {
  /** Markers past the lab bound. */
  nOutOfLab: number;
  /** 3 when the sole out-of-lab marker is past alarm. */
  worstOutOfLabRank: number;
  /** Markers past optimal but within lab. */
  nWithinLab: number;
  /** ...of which, past the lab↔optimal midpoint. */
  nWithinLabPastMid: number;
}

export const emptyTally = (): SeverityTally => ({
  nOutOfLab: 0,
  worstOutOfLabRank: 0,
  nWithinLab: 0,
  nWithinLabPastMid: 0,
});

/**
 * Score one deviating marker into the tally. `rank` is its deviationRank in the
 * direction the gauge counted it; `pastMid` is isPastMidpoint the same way.
 */
export function tallyMarker(t: SeverityTally, rank: number, pastMid: boolean): void {
  if (rank >= 2) {
    t.nOutOfLab += 1;
    t.worstOutOfLabRank = Math.max(t.worstOutOfLabRank, rank);
  } else {
    t.nWithinLab += 1;
    if (pastMid) t.nWithinLabPastMid += 1;
  }
}

/**
 * Glucose-style hybrid over a tally of same-direction markers: out-of-lab
 * markers count-stack; an all-within-lab read is graded by the lab↔optimal
 * midpoint.
 *   • all within lab: a single marker reads 55 (mild) / 60 (past midpoint);
 *     two-or-more read 65 / 72 / 80 by how many are past midpoint.
 *     55, not 50, so a mild single needle clears the green/yellow seam.
 *   • any out of lab: one 85 (95 if that one is past alarm), two 97,
 *     three-or-more 99.
 * Callers handle the no-deviation case themselves (it is the gauge's balanced
 * state, with its own confidence tiers).
 */
export function hybridProbability(t: SeverityTally): number {
  if (t.nOutOfLab >= 1) {
    if (t.nOutOfLab === 1) return t.worstOutOfLabRank >= 3 ? 95 : 85;
    if (t.nOutOfLab === 2) return 97;
    return 99;
  }
  if (t.nWithinLab === 1) return t.nWithinLabPastMid >= 1 ? 60 : 55;
  return t.nWithinLabPastMid >= 2 ? 80 : t.nWithinLabPastMid === 1 ? 72 : 65;
}
